import { PlaybookPage as PlaybookPageComponent } from '@carboncopyco/components';
import {
  useLazyGetPlayQuery,
  useLazyGetPlaybookQuery,
} from '@carboncopyco/data';
import type { Play } from '@carboncopyco/types';
import { useEffect, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import WorkspaceLayout from '../../components/Workspaces/WorkspaceLayout';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { usePlaybookPresentations } from '../../hooks/usePlaybookPresentations';

type PlayDeeplinkMode = 'preview' | 'play';

export const PlaybookPage = () => {
  const { organizationId, workspaceId, playbookId } = useParams();
  const [searchParams] = useSearchParams();
  const [getPlay] = useLazyGetPlayQuery();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [getPlaybook, { data: playbook }] = useLazyGetPlaybookQuery();

  useEffect(() => {
    if (organizationId && workspaceId && playbookId) {
      getPlaybook({
        organizationId,
        workspaceId,
        id: playbookId,
      });
    }
  }, []);

  const {
    navigateToWorkspace,
    navigateToPlaybookPage,
    navigateToFolder,
    links,
  } = useCustomNavigate();

  const { handlePlayPlaybook, handlePreviewPlaybook } =
    usePlaybookPresentations({
      organizationId,
    } as { organizationId: string });

  const navigateByDeeplink = async (
    workspaceId: string,
    mode: PlayDeeplinkMode,
    playData: Play
  ) => {
    if (!playbookId) return;
    await (mode === 'preview'
      ? handlePreviewPlaybook(workspaceId, playbookId, playData)
      : handlePlayPlaybook({ playSubset: playData }));
  };

  const handleDeepLink = async (workspaceId: string) => {
    const mode = searchParams.get('mode') as PlayDeeplinkMode;
    const playId = searchParams.get('playId');
    if (!mode || !organizationId || isLoading || !playbookId) return;
    try {
      setIsLoading(true);
      if (!playId && mode === 'preview') {
        await handlePreviewPlaybook(workspaceId, playbookId);
      }
      if (!playId) return;
      const play = await getPlay({
        organizationId,
        playId,
      }).unwrap();
      await navigateByDeeplink(workspaceId, mode, play);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    workspaceId && handleDeepLink(workspaceId);
  }, [location.key]);

  if (!organizationId || !workspaceId || !playbookId) {
    return null;
  }

  return (
    <WorkspaceLayout>
      <PlaybookPageComponent
        playbookId={playbookId}
        goToPlaybookPage={(id) => navigateToPlaybookPage(id, workspaceId)}
        goToPlaybookPreview={(play) =>
          handlePreviewPlaybook(workspaceId, playbookId, play)
        }
        onGoBack={() => navigateToWorkspace(workspaceId)}
        organizationId={organizationId}
        rootPath={links.getPlaybookPageRoot(playbookId)}
        workspaceId={workspaceId}
        goToWorkspacePage={navigateToWorkspace}
        goToFolderPage={navigateToFolder}
        demoPageLink={links.getDemoPageLink}
        tourPageLink={links.getTourPageLink}
        playPresentation={(play) => handlePlayPlaybook({ playSubset: play })}
        readOnly={playbook?.readOnly}
      />
    </WorkspaceLayout>
  );
};
